import { Spinner } from "@chakra-ui/react";
import styled from "@emotion/styled";
import { useMemo, useState } from "react";
import { FaTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { ProductsAPI } from "../../../api/Products";
import { TagsAtom } from "../../../atoms/TagsAtom";
import { IProduct, ProductType, StripeProduct } from "../../../generated/Api";
import { ITag } from "../../../types/TagTypes";
import Warning from "../../Modal/container/Warning";
import { useModal } from "../../Modal/Modal";
import TagSelect from "../../TagSelect/TagSelect";

const ProductListCard = styled.div`
  display: flex;
  align-items: flex-start;

  width: 100%;
  grid-gap: 24px;

  .tag-container {
    display: flex;
    flex-direction: column;

    width: 100%;
  }
`;

const ProductName = styled.div`
  font-size: 18px;
  line-height: 40px;
  color: #222222;
  font-weight: 400;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  width: 100%;
`;

const IconButton = styled.div`
  width: 40px;
  height: 40px;

  border-radius: 4px;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;

  transition: all 0.6s;

  &:hover {
    background: #dc143c;
    transition: all 0.6s;

    > svg {
      color: #fff;
    }
  }

  > svg {
    color: #979797;
    width: 20px;
    height: 20px;

    transition: all 0.6s;
  }
`;

interface Props {
  product: IProduct;
  refreshProducts: () => Promise<void>;
}

export function StripeProductItem(props: Props) {
  const { product, refreshProducts } = props;
  const modal = useModal();
  const tagsPool = useRecoilValue(TagsAtom);

  const productTags = useMemo(() => {
    return (product as StripeProduct).tagIds
      .map((pt) => tagsPool.find((t) => `${t._id}` === pt))
      .filter((t) => t);
  }, [product, tagsPool]);

  const [isLoading, setIsLoading] = useState(false);

  async function deleteProduct() {
    setIsLoading(true);

    const isDeleted = await ProductsAPI.remove(product._id);

    if (isDeleted) {
      await refreshProducts();
    } else {
      toast("An error occured when deleting your product!", {
        type: "error",
      });
    }
    modal.popModal();
    setIsLoading(false);
  }

  function handleClickDelete() {
    modal.pushModal(
      <Warning
        icon={"./icons/delete.svg"}
        title="Delete Product"
        description="Are you sure you want to delete this product?"
        buttonsName={["No", "Yes"]}
        onOkClicked={deleteProduct}
        onCancelClicked={() => modal.popModal()}
      />
    );
  }

  async function handleChoseTags(tags: ITag[]) {
    const isUpdated = await ProductsAPI.update(product._id, {
      ...product,
      type: ProductType.STRIPE,
      tagIds: tags.map((t) => `${t._id}`),
    });

    if (isUpdated) {
      await refreshProducts();
    } else {
      toast("An error occured when updating your product tags!", {
        type: "error",
      });
    }
  }

  return (
    <ProductListCard>
      <ProductName>{product.productName}</ProductName>
      <div className="tag-container">
        <TagSelect
          cancelTagLoading={true}
          tags={productTags}
          preserveSystemTags
          handleChoseTags={handleChoseTags}
        />
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <IconButton onClick={handleClickDelete}>
          <FaTrashAlt />
        </IconButton>
      )}
    </ProductListCard>
  );
}
